/**
 * Queue column preferences (Task 3) — show/hide + order, persisted per device in
 * localStorage. Pure + DOM-free (injectable Storage) so it's unit-tested in the
 * node project. Normalisation is tolerant of schema drift: persisted prefs are
 * reconciled against the canonical key list so adding/removing a column never
 * breaks a saved layout (new columns appear, removed ones drop out).
 */
import type { KeyValueStore } from "./savedViews.js";

export interface ColumnPrefs {
  /** Column keys in display order (includes required + hidden keys). */
  order: string[];
  /** Hidden column keys (required keys are ignored here — they always show). */
  hidden: string[];
}

const STORAGE_KEY = "cairn.queue.columns.v1";

/** Reconcile (possibly partial/stale) prefs against the canonical key list. */
export function normalizeColumnPrefs(prefs: Partial<ColumnPrefs> | null | undefined, allKeys: string[]): ColumnPrefs {
  const known = new Set(allKeys);
  const order = (Array.isArray(prefs?.order) ? prefs!.order : []).filter((k): k is string => typeof k === "string" && known.has(k));
  for (const k of allKeys) if (!order.includes(k)) order.push(k); // append new columns
  const hidden = (Array.isArray(prefs?.hidden) ? prefs!.hidden : []).filter((k): k is string => typeof k === "string" && known.has(k));
  return { order, hidden };
}

export function loadColumnPrefs(store: KeyValueStore, allKeys: string[]): ColumnPrefs {
  try {
    const raw = store.getItem(STORAGE_KEY);
    return normalizeColumnPrefs(raw ? (JSON.parse(raw) as Partial<ColumnPrefs>) : null, allKeys);
  } catch {
    return normalizeColumnPrefs(null, allKeys);
  }
}

export function saveColumnPrefs(store: KeyValueStore, prefs: ColumnPrefs): void {
  store.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

/** Keys to render, in order. Required keys always show even if marked hidden. */
export function visibleColumnKeys(prefs: ColumnPrefs, requiredKeys: ReadonlySet<string>): string[] {
  const hidden = new Set(prefs.hidden);
  return prefs.order.filter((k) => requiredKeys.has(k) || !hidden.has(k));
}

/** Toggle a column's visibility (required columns can't be hidden). */
export function toggleColumn(prefs: ColumnPrefs, key: string, requiredKeys: ReadonlySet<string>): ColumnPrefs {
  if (requiredKeys.has(key)) return prefs;
  const hidden = new Set(prefs.hidden);
  if (hidden.has(key)) hidden.delete(key);
  else hidden.add(key);
  return { ...prefs, hidden: [...hidden] };
}

/** Move a column one slot up (-1) or down (+1) in the order. */
export function moveColumn(prefs: ColumnPrefs, key: string, dir: -1 | 1): ColumnPrefs {
  const order = [...prefs.order];
  const i = order.indexOf(key);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= order.length) return prefs;
  [order[i], order[j]] = [order[j]!, order[i]!];
  return { ...prefs, order };
}
